/**
 * <TutorApplicationQuestionPage> — une question écrite de la candidature (page).
 *
 * Le profil (étape 1, l'ancien `ApplicationPersonalInfos`) et les entretiens écrits de motivation et
 * de pédagogie (étapes 2 et 4, l'ancien `InterviewScreen` sans vidéo) se répondent une question à la
 * fois, dans l'ancien `FormStepper`. Une seule page pour toutes ces questions, sous la règle des
 * parcours du DS : UN en-tête (`<Header steps>`), la question en titre, la croix à la première
 * question et la flèche aux suivantes, la barre de progression à la place du filet.
 *
 * Le corps dit comment on répond (`answer`) :
 *  - `choice` : une liste d'options, en cartes (`<OptionCard>`) ou en pilules (`<ChoiceChip>`) pour
 *    les réponses courtes (une année, un niveau d'étude) ; une ou plusieurs ; une option refusée
 *    quand le maximum est atteint (« 2 compétences ») ;
 *  - `fields` : des champs à écrire (coordonnées, notes du bac de français, établissement, SIRET) ;
 *  - `languages` : les langues et leur niveau (`<TutorLanguageList>`).
 * Au-dessus, un interrupteur quand la question en a un (« J'ai un équivalent du baccalauréat passé à
 * l'étranger », « J'ai déjà le statut auto-entrepreneur ») et sa note ; en dessous, la précision
 * libre d'un choix (« Pourquoi ? »).
 *
 * Dans un entretien (motivation, pédagogie), le champion de l'étape pose la question, en pied, dans sa bulle
 * (`askedBy`, `<TutorApplicationChampionAsks>`), comme l'ancien `InterviewScreen` : l'en-tête porte
 * alors le nom de l'étape. Le profil n'a pas de champion, la question y reste le titre de l'en-tête.
 *
 * Le bouton du pied (`action`) n'est là que quand la question en a un : une question à choix unique
 * passe à la suivante d'un geste, comme aujourd'hui. Une erreur du serveur s'affiche au-dessus de lui.
 * Quitter avant d'avoir envoyé se confirme (`exitConfirm`).
 *
 * Contrôlée de bout en bout.
 */
import React from "react"

import {
    type UITutorApplicationAsker,
    type UITutorApplicationOption,
    type UITutorLanguage,
    type UITutorLanguageLevel,
} from "../../../ports/primary/ui/types/tutorCandidature"
import { Banner } from "../../atoms/Banner"
import { Button } from "../../atoms/Buttons/Button"
import { Stack } from "../../atoms/containers/Stack"
import { Switch } from "../../atoms/Switch"
import { TextInput } from "../../atoms/TextInput"
import { Text } from "../../atoms/typography/Text"
import { Card } from "../../molecules/Card"
import { ConfirmDialog } from "../../molecules/ConfirmDialog"
import { Header } from "../../molecules/Header"
import { OptionCard } from "../../molecules/OptionCard"
import { TutorApplicationChampionAsks } from "../../molecules/TutorApplicationChampionAsks"
import { TutorChoiceStep } from "../../organisms/TutorChoiceStep"
import { TutorLanguageList } from "../../organisms/TutorLanguageList"
import { PageTemplate } from "../../templates/PageTemplate"

/** Une question et ses réponses : la colonne d'un parcours à étapes. */
const TUTOR_APPLICATION_QUESTION_WIDTH = 560

export const TUTOR_APPLICATION_QUESTION_TEXTS = {
    next: "Suivant",
    submit: "Terminer",
    exitTitle: "Veux-tu vraiment quitter ?",
    exitConfirm: "Quitter",
    exitCancel: "Annuler",
}

type TutorApplicationField = {
    id: string
    /** Le libellé au-dessus du champ, quand il en a un (« Et à l'oral ? »). */
    label?: string
    placeholder: string
    value: string
    error?: string
    keyboard?: "default" | "phone-pad" | "decimal-pad"
}

type TutorApplicationAnswer =
    | {
          kind: "choice"
          options: UITutorApplicationOption[]
          selectedIds: string[]
          /** `chips` pour des réponses courtes. Défaut `cards`. */
          layout?: "cards" | "chips"
          onToggle: (id: string) => void
      }
    | { kind: "fields"; fields: TutorApplicationField[]; onChange: (id: string, value: string) => void }
    | {
          kind: "languages"
          languages: UITutorLanguage[]
          available: { id: string; name: string }[]
          onAdd: (id: string) => void
          onRemove: (id: string) => void
          onChangeLevel: (id: string, level: UITutorLanguageLevel) => void
          showErrors?: boolean
      }

/**
 * « Veux-tu vraiment quitter ? », ouverte : la même forme sur toutes les pages de la candidature qui
 * se quittent (une question écrite, une question vidéo), la phrase dite par la page qui la pose.
 */
export type TutorApplicationExitConfirm = {
    visible: boolean
    message: string
    onConfirm: () => void
    onCancel: () => void
}

export type TutorApplicationQuestionPageProps = {
    /** La question : le titre de l'en-tête, ou la bulle du champion quand il la pose (`askedBy`). */
    question: string
    /** Un entretien : le champion de l'étape pose la question, et l'en-tête dit l'étape. Absent au profil. */
    askedBy?: UITutorApplicationAsker
    /** Son rang parmi les questions de l'étape, à partir de 1. */
    index: number
    total: number
    answer: TutorApplicationAnswer
    /** L'interrupteur au-dessus des réponses, et sa note. */
    toggle?: { label: string; checked: boolean; onChange: (next: boolean) => void; note?: string }
    /** La précision libre sous un choix. */
    precision?: { placeholder: string; value: string; onChange: (text: string) => void }
    /** Le bouton du pied. Absent : la question passe à la suivante d'un geste. */
    action?: { label: string; disabled?: boolean; loading?: boolean; onPress: () => void }
    /** Le message d'erreur du serveur, après un envoi. */
    error?: string | null
    /** Flèche (question suivante) ou croix (première question) : l'écran sait laquelle. */
    onBack?: () => void
    onClose?: () => void
    /** « Veux-tu vraiment quitter ? », ouverte. */
    exitConfirm?: TutorApplicationExitConfirm
    testID?: string
}

export function TutorApplicationQuestionPage(props: TutorApplicationQuestionPageProps) {
    const {
        question,
        askedBy,
        index,
        total,
        answer,
        toggle,
        precision,
        action,
        error,
        onBack,
        onClose,
        exitConfirm,
        testID,
    } = props

    const footer =
        action != null || (error != null && error !== "") ? (
            <Stack gap="3">
                {error != null && error !== "" && (
                    <Banner color="danger" tinted icon="exclamation-circle" align="start" message={error} />
                )}
                {action != null && (
                    <Button
                        label={action.label}
                        fullWidth
                        disabled={action.disabled}
                        loading={action.loading}
                        onPress={action.onPress}
                    />
                )}
            </Stack>
        ) : undefined

    return (
        <>
            <PageTemplate
                testID={testID}
                header={
                    <Header title={askedBy?.stepTitle ?? question} steps={{ step: index, total, onBack, onClose }} />
                }
                footer={footer}
                padding="4"
                maxContentWidth={TUTOR_APPLICATION_QUESTION_WIDTH}
                // On répond clavier ouvert : « Suivant » reste au-dessus du clavier, pavé numérique compris.
                keyboardAware
            >
                <Stack gap="4">
                    {askedBy != null && (
                        <TutorApplicationChampionAsks champion={askedBy.champion} question={question} />
                    )}
                    {toggle != null && (
                        <Stack gap="2">
                            <Card p="4">
                                <Switch label={toggle.label} checked={toggle.checked} onChange={toggle.onChange} />
                            </Card>
                            {toggle.note != null && (
                                <Text size="text-sm" color="text-muted">
                                    {toggle.note}
                                </Text>
                            )}
                        </Stack>
                    )}
                    <Answer answer={answer} />
                    {precision != null && (
                        <TextInput
                            value={precision.value}
                            onChangeText={precision.onChange}
                            placeholder={precision.placeholder}
                            multiline
                            numberOfLines={4}
                        />
                    )}
                </Stack>
            </PageTemplate>
            {exitConfirm != null && <TutorApplicationExitDialog exitConfirm={exitConfirm} />}
        </>
    )
}

/** La confirmation de sortie d'une page de la candidature : mêmes mots, même bouton rouge partout. */
export function TutorApplicationExitDialog(props: { exitConfirm: TutorApplicationExitConfirm }) {
    const { visible, message, onConfirm, onCancel } = props.exitConfirm
    return (
        <ConfirmDialog
            visible={visible}
            title={TUTOR_APPLICATION_QUESTION_TEXTS.exitTitle}
            message={message}
            confirmLabel={TUTOR_APPLICATION_QUESTION_TEXTS.exitConfirm}
            cancelLabel={TUTOR_APPLICATION_QUESTION_TEXTS.exitCancel}
            danger
            onConfirm={onConfirm}
            onCancel={onCancel}
        />
    )
}

function Answer(props: { answer: TutorApplicationAnswer }) {
    const { answer } = props

    if (answer.kind === "fields") {
        return (
            <Stack gap="3">
                {answer.fields.map((field) => (
                    <TextInput
                        key={field.id}
                        label={field.label}
                        placeholder={field.placeholder}
                        value={field.value}
                        error={field.error}
                        keyboardType={field.keyboard ?? "default"}
                        onChangeText={(text) => answer.onChange(field.id, text)}
                    />
                ))}
            </Stack>
        )
    }

    if (answer.kind === "languages") {
        return (
            <TutorLanguageList
                languages={answer.languages}
                available={answer.available}
                onAdd={answer.onAdd}
                onRemove={answer.onRemove}
                onChangeLevel={answer.onChangeLevel}
                showErrors={answer.showErrors}
            />
        )
    }

    if (answer.layout === "chips") {
        return <TutorChoiceStep options={answer.options} selectedIds={answer.selectedIds} onToggle={answer.onToggle} />
    }

    return (
        <Stack gap="2">
            {answer.options.map((option) => (
                <OptionCard
                    key={option.id}
                    label={option.label}
                    description={option.detail}
                    selected={answer.selectedIds.includes(option.id)}
                    disabled={option.disabled}
                    onPress={() => answer.onToggle(option.id)}
                />
            ))}
        </Stack>
    )
}
